import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { openPdf } from "@/lib/pdf";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import {
  Activity, Bell, UserPlus, LogIn, Mail, Scale, FileUp, Sparkles,
  Shield, Filter, Search, RefreshCcw, FileDown,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const KIND_META = {
  ai:           { icon: Sparkles,  color: "#7A4EA6", label: "Copilot" },
  notification: { icon: Bell,      color: "#0F5E39", label: "Notification" },
  visitor:      { icon: UserPlus,  color: "#C9A24B", label: "Visitor" },
  attendance:   { icon: LogIn,     color: "#0F5E39", label: "Attendance" },
  email:        { icon: Mail,      color: "#8A6600", label: "Email" },
  ledger:       { icon: Scale,     color: "#C9A24B", label: "Metal" },
  import:       { icon: FileUp,    color: "#8A6600", label: "Import" },
};

const KIND_ORDER = ["ai", "notification", "visitor", "attendance", "email", "ledger", "import"];

export default function AuditHubPage() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [meta, setMeta] = useState({ masked: false, role: "" });
  const [loading, setLoading] = useState(false);
  const [hours, setHours] = useState(72);
  const [q, setQ] = useState("");
  const [selectedKinds, setSelectedKinds] = useState(new Set());
  const [live, setLive] = useState(false);
  const navigate = useNavigate();

  // SSE live feed — prepend new events at the top when enabled
  useEffect(() => {
    if (!live) return;
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const token = localStorage.getItem("token");
    let stopped = false;
    let reader = null;
    (async () => {
      try {
        const res = await fetch(`${backend}/api/audit-hub/stream`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          credentials: "include",
        });
        if (!res.ok || !res.body) return;
        reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = "";
        // eslint-disable-next-line no-constant-condition
        while (!stopped) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          let idx;
          while ((idx = buf.indexOf("\n\n")) !== -1) {
            const frame = buf.slice(0, idx); buf = buf.slice(idx + 2);
            let ev = null, data = "";
            for (const line of frame.split("\n")) {
              if (line.startsWith("event: ")) ev = line.slice(7).trim();
              else if (line.startsWith("data: ")) data += line.slice(6);
            }
            if (ev === "audit" && data) {
              try {
                const item = JSON.parse(data);
                setItems((prev) => [item, ...prev].slice(0, 200));
                setCounts((c) => ({ ...c, [item.kind]: (c[item.kind] || 0) + 1 }));
              } catch (_) { /* noop */ }
            }
          }
        }
      } catch (_) { /* noop */ }
    })();
    return () => { stopped = true; try { reader?.cancel?.(); } catch (_) { /* noop */ } };
  }, [live]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ since_hours: String(hours), limit: "200" });
      if (selectedKinds.size) params.set("kinds", Array.from(selectedKinds).join(","));
      if (q.trim()) params.set("q", q.trim());
      const { data } = await api.get(`/audit-hub/?${params}`);
      setItems(data.items || []);
      setCounts(data.counts_by_kind || {});
      setMeta({ masked: data.customer_data_masked, role: data.role });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };

  const sharePdf = async () => {
    try {
      await openPdf(`/api/reports/audit-hub.pdf?hours=${hours}`);
    } catch (e) {
      toast.error("Failed to generate PDF");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [hours, selectedKinds, q]);

  const toggle = (k) => {
    const next = new Set(selectedKinds);
    next.has(k) ? next.delete(k) : next.add(k);
    setSelectedKinds(next);
  };

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-4" data-testid="audit-hub-page">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-full bg-[#0F5E39] text-white flex items-center justify-center"><Activity size={18}/></div>
        <div className="flex-1">
          <div className="font-serif text-2xl">Audit Hub</div>
          <div className="text-xs text-[#4A5A50]">One inbox for AI, notifications, visitors, attendance, email, metal, imports</div>
        </div>
        <Button variant="outline" size="sm" onClick={load} className="rounded-full" data-testid="audit-refresh"><RefreshCcw size={14} className="mr-1"/>Refresh</Button>
        <Button
          size="sm"
          variant={live ? "default" : "outline"}
          className={`rounded-full ${live ? "bg-[#0F5E39] hover:bg-[#083F22] text-white" : ""}`}
          onClick={() => setLive((v) => !v)}
          data-testid="audit-live-toggle"
        >
          {live ? "Live · ON" : "Live · OFF"}
        </Button>
        <Button size="sm" onClick={sharePdf} className="rounded-full bg-[#C9A24B] hover:bg-[#B88C34] text-[#0F1E15]" data-testid="audit-share-pdf">
          <FileDown size={14} className="mr-1"/>Share PDF
        </Button>
      </div>

      {meta.masked && (
        <div className="rounded-xl bg-[#FFF6E1] border border-[#C9A24B] px-3 py-2 flex items-center gap-2 text-xs text-[#8A6600]" data-testid="audit-mask-notice">
          <Shield size={14}/> Customer identity and pricing are masked in this view for your role ({meta.role}).
        </div>
      )}

      <Card><CardContent className="p-3 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 px-2 rounded-full bg-white border border-[#E4DFCF]">
            <Search size={14} className="text-[#4A5A50]"/>
            <input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search title/body…"
              className="h-9 outline-none text-sm bg-transparent w-56" data-testid="audit-search"/>
          </div>
          <select value={hours} onChange={(e) => setHours(Number(e.target.value))} className="h-9 rounded-full border border-[#E4DFCF] px-3 text-sm bg-white" data-testid="audit-window">
            <option value={24}>Last 24h</option>
            <option value={72}>Last 3 days</option>
            <option value={168}>Last 7 days</option>
            <option value={720}>Last 30 days</option>
          </select>
          <div className="ml-auto text-xs text-[#4A5A50]"><Filter size={12} className="inline mr-1"/>Kinds:</div>
          {KIND_ORDER.map((k) => {
            const meta = KIND_META[k];
            const active = selectedKinds.has(k);
            const Icon = meta.icon;
            const count = counts[k] || 0;
            return (
              <button key={k} onClick={() => toggle(k)}
                className={`text-xs px-3 py-1 rounded-full inline-flex items-center gap-1 border ${active ? "bg-[#0F5E39] text-white border-[#0F5E39]" : "bg-white text-[#4A5A50] border-[#E4DFCF] hover:border-[#0F5E39]/40"}`}
                data-testid={`audit-kind-${k}`}>
                <Icon size={12}/>{meta.label} {count > 0 && <span className="opacity-70">· {count}</span>}
              </button>
            );
          })}
        </div>
      </CardContent></Card>

      <div className="space-y-2">
        {loading && <div className="text-sm text-[#4A5A50] italic">Loading…</div>}
        {!loading && items.length === 0 && <p className="text-sm text-[#4A5A50] italic" data-testid="audit-empty">No events in this window.</p>}
        {items.map((it, i) => {
          const meta = KIND_META[it.kind] || KIND_META.notification;
          const Icon = meta.icon;
          return (
            <Card key={i}><CardContent className="p-3 flex items-start gap-3" data-testid={`audit-row-${i}`}>
              <div className="mt-1 h-8 w-8 rounded-full flex items-center justify-center shrink-0" style={{ background: `${meta.color}22`, color: meta.color }}>
                <Icon size={16}/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-serif text-sm">{it.title}</div>
                {it.body && <div className="text-xs text-[#4A5A50] mt-0.5 line-clamp-2">{it.body}</div>}
                <div className="text-[10px] uppercase tracking-widest text-[#4A5A50] mt-1">
                  {(it.at || "").slice(0, 19).replace("T", " ")}
                  {it.actor?.name && <> · {it.actor.name}</>}
                  {it.actor?.role && <> · {it.actor.role}</>}
                </div>
              </div>
              {it.ref?.path && it.ref.path !== "/audit-hub" && (
                <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => navigate(it.ref.path)}>Open</Button>
              )}
            </CardContent></Card>
          );
        })}
      </div>
    </div>
  );
}
